import type { Log } from '@scayle/storefront-nuxt'

export interface useCookieOptions {
  ttlDays?: number
  req?: any
  res?: any
  path?: string
  log?: Log
}

export type StringCookie = ReturnType<typeof useStringCookie>

const COOKIE_REF_DEFAULTS = {
  ttlDays: 30,
  path: '/',
} as useCookieOptions

export default function useStringCookie(
  cookieName: string,
  options: useCookieOptions = {},
) {
  let init = true
  let current: string | undefined

  // merge with default options
  options = {
    ...COOKIE_REF_DEFAULTS,
    ...options,
  }

  // get request and response from context if not provided
  if (!options.req && !options.res) {
    const { ssrContext } = useNuxtApp()
    const { req, res } = ssrContext?.event?.node || {}
    options = { ...options, req, res }
  }

  return {
    get() {
      if (!init) {
        return current
      }
      const doc = import.meta.client ? document : undefined
      const cookies = options.req?.headers?.cookie || doc?.cookie || ''
      const map = parseCookieString(cookies)
      current = map[cookieName]
      // options.log?.debug(`Read cookie "${cookieName}": "${current}"`)
      init = false
      return current
    },
    set(value: string | undefined) {
      init = false
      current = value
      setCookie(cookieName, value, options)
    },
    delete: () => {
      init = false
      current = undefined
      setCookie(cookieName, '', { ...options, ttlDays: 0 })
    },
  }
}

function setCookie(
  cookieName: string,
  value: string | undefined,
  options: useCookieOptions,
) {
  const date = new Date()
  date.setTime(date.getTime() + options.ttlDays! * 24 * 60 * 60 * 1000)
  const encodedValue = value ? encodeURIComponent(value) : value
  const cookieString = [
    `${cookieName}=${encodedValue}`,
    `path=${options.path}`,
    `expires=${date.toUTCString()};`,
  ].join('; ')

  if (import.meta.client) {
    document.cookie = cookieString
  } else if (options.res) {
    const existing = options.res.getHeader('Set-Cookie')
    const existingArr = Array.isArray(existing)
      ? existing.flat()
      : existing != null
        ? [existing]
        : []

    options.res.setHeader('Set-Cookie', [
      ...existingArr.filter(Boolean),
      cookieString,
    ])
  }
}

function parseCookieString(cookieString: string) {
  return cookieString.split(';').reduce(
    (map, cookie) => {
      const [name, value] = cookie.split('=')
      const trimmedName = name?.trim()
      if (trimmedName && typeof value !== 'undefined') {
        map[trimmedName] = decodeURIComponent(value.trim())
      }
      return map
    },
    {} as Record<string, string>,
  )
}
